import { useConfigStore } from '@/stores';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { useOrientation, useTickSoundInteraction } from '@/hooks';
import type { ListTypeListItem } from '@/types/list-type';
import { memo, useCallback } from 'react';
import { formatKoreanDateTTS, formatListTypeDateRange } from '@/utils/date-time';
import { getS3UploadUrl, isS3UploadUrl } from '@/utils/s3Config';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from '@/components/icons/calendarIcon';

interface ListTypeCarouselItemProps {
    content: ListTypeListItem;
    tabIndex: number;
    onClick?: (data: ListTypeListItem) => void;
}

const ListTypeCarouselItem = memo(({ content, tabIndex, onClick }: ListTypeCarouselItemProps) => {
    const { t } = useTranslation();

    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const { isHorizontal } = useOrientation();
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();

    const url = content?.contentsUrl?.url;
    const urlPreview: string | null | undefined = url ? (isS3UploadUrl(url) ? url : getS3UploadUrl(url)) : '';

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const parent = e.currentTarget;
            parent.classList.add('poi-active');
            handleInteraction(e, () => onClick?.(content));
        },
        [handleInteraction, onClick, content]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => handleKeyboardInteraction(e, () => onClick?.(content)),
        [handleKeyboardInteraction, onClick, content]
    );

    const ariaLabel = `${content.title}, ${formatKoreanDateTTS(content.eventDateBegin, content.eventDateEnd, t)}`;

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            key={content.id}
            tabIndex={tabIndex}
            data-interactive
            data-playsound="ting"
            className={clsx(styles.wrapper, {
                [styles['wrapper__contrast']]: contrast,
                [styles['wrapper__vertical']]: !isHorizontal,
                [styles['wrapper__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
            })}
            aria-label={ariaLabel}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <div
                className={clsx(styles['wrapper__image'], {
                    [styles['wrapper__image__vertical']]: !isHorizontal,
                })}
            >
                {urlPreview ? (
                    <img src={urlPreview} alt={'img error'} loading="lazy" />
                ) : (
                    <div style={{ width: '100%', height: '100%', background: '#f4f4f4' }} />
                )}
            </div>

            <div
                className={clsx(styles['wrapper__info'], {
                    [styles['wrapper__info__contrast']]: contrast,
                    [styles['wrapper__info__vertical']]: !isHorizontal,
                })}
            >
                <div
                    className={clsx(styles['wrapper__info--title'], {
                        [styles['wrapper__info--title__vertical']]: !isHorizontal,
                    })}
                >
                    {content.title.length > 24 ? `${content.title.slice(0, 24)}...` : content.title}
                </div>
                <div className={styles['wrapper__info--time']}>
                    <CalendarIcon width={18} height={18} color={!contrast ? '#444444' : '#ffffff'} />
                    {formatListTypeDateRange(content.eventDateBegin, content.eventDateEnd, t)}
                </div>
            </div>
        </div>
    );
});

ListTypeCarouselItem.displayName = 'ListTypeCarouselItem';

export default ListTypeCarouselItem;
